import styled from '@emotion/styled'
import { Black, Gray700 } from '@mehilainen/mds-customer/colors'
import { White } from '@mehilainen/mds-customer/colors'
import { ExclamationTriangle } from '@mehilainen/mds-customer/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { NodeAutocompleteResult, PublicNode } from '../../../__generated__/api'
import { AppointmentSearchMode } from '../../../state/search/atoms'
import { useModalEvents } from '../../../xstate/events'
import { useIsMobile } from '../../hooks/useBreakpoint'
import { useOHC } from '../../hooks/useOHC'
import { useNodeSearch } from '../../hooks/useSearch'
import { useEhoitoNavigationEvents } from '../../services/omApi'
import AnchorButton from '../AnchorButton/AnchorButton'
import Avatar from '../Avatar/Avatar'
import { CenteredColumnFlex } from '../Layout/Layout'
import ResponsiveModal from '../Modal/ResponsiveModal/ResponsiveModal'
import EventButton from '../OMLight/EventButton'
import { PublicNodeIcon } from '../Search/PublicNodeIcon'
import Search from '../Search/Search'
import { SearchTreeItem } from '../Search/types'
import { convertSearchNodes } from '../Search/utils'
import { Text } from '../Typography/Typography'

const SearchContainer = styled.div<{ isMobile?: boolean }>`
  padding: ${(props) => (props.isMobile ? '0 0 24px 0' : '32px 24px 32px 24px')};
  background: ${(props) => (props.isMobile ? White : 'transparent')};
`

const NoteContainer = styled(CenteredColumnFlex)`
  text-align: center;
  gap: 24px;

  .MuiSvgIcon-root {
    width: 48px;
    height: 48px;
    color: ${Black};
  }
`

const NoteTitle = styled.div`
  font-size: 1.938rem;
  line-height: 1.4;
`

const BreadcrumbTitle = styled(Text)`
  display: block;
  padding: 16px 0 8px 0;
`

interface Props {
  listedNodes: PublicNode[]
  favoriteNodes: PublicNode[]
  breadcrumb: Array<SearchTreeItem<PublicNode>>
  onSelect: (service: any /* FIXME: typing */) => void
  onClose: () => void
}

const callbackItemId = AppointmentSearchMode.CALLBACK

const SearchSelect: React.FC<React.PropsWithChildren<Props>> = ({
  listedNodes,
  favoriteNodes,
  breadcrumb,
  onSelect,
  onClose,
}) => {
  const { t, i18n } = useTranslation()
  const { isOHCSide } = useOHC()
  const nodeSearch = useNodeSearch(isOHCSide)
  const isMobile = useIsMobile()
  const { events } = useEhoitoNavigationEvents()
  const { openCallbackModal } = useModalEvents()

  const noteProvider = (item: SearchTreeItem<PublicNode>) => {
    return (
      <NoteContainer>
        <ExclamationTriangle />
        <NoteTitle>{t('component.serviceSelect.noteTitle')}</NoteTitle>
        <div>{t('component.serviceSelect.noteText', { name: item.name.toLocaleLowerCase() })}</div>
        <div>
          <AnchorButton href="https://www.mehilainen.fi/tietoa-asiakkaalle/palvelunumerot">
            {t('component.serviceSelect.noteLink')}
          </AnchorButton>
        </div>
      </NoteContainer>
    )
  }

  const callbackItem: SearchTreeItem<PublicNode> = {
    name: t('common.callback'),
    id: callbackItemId,
    item: {
      id: callbackItemId,
    } as PublicNode,
  }

  const popularData = [
    ...convertSearchNodes(favoriteNodes, t('component.serviceSearch.secondaryGroupLabel')),
    callbackItem,
  ]

  const allData = convertSearchNodes(
    listedNodes,
    t('component.serviceSearch.secondaryGroupLabel')
  ).sort((item, other) => item.name.localeCompare(other.name, i18n.language))

  const breadcrumbHighlightProvider = (item: SearchTreeItem<PublicNode> | undefined) =>
    item?.item?.referralLinks.some((link) => link === 'ttk') && events?.length ? (
      <>
        <BreadcrumbTitle $size={200} $height="Medium" $uppercase $color={Gray700}>
          {t('component.serviceSearch.breadcrumbHighlight')}
        </BreadcrumbTitle>
        <EventButton events={events} />
        <BreadcrumbTitle $size={200} $height="Medium" $uppercase $color={Gray700}>
          {t('component.serviceSearch.otherHealthExaminations')}
        </BreadcrumbTitle>
      </>
    ) : null

  const mapDataToListProps = (item: SearchTreeItem<PublicNode>) => {
    const title = item.name
    const description = item.caption || undefined
    const startIcon = <PublicNodeIcon item={item} />
    const endIcon = item.children && item.children.length > 0 ? undefined : false

    return { title, description, startIcon, endIcon }
  }

  const mapSearchResultsToListProps = (item: NodeAutocompleteResult) => {
    const title = item.keyword
    const description = item.practitionerData?.title || undefined
    const startIcon = item.practitionerData ? (
      <Avatar image={item.practitionerData.image} />
    ) : (
      <PublicNodeIcon src={item.nodeData?.icon} />
    )

    const nodeDataHasChildren = item.nodeData && item.nodeData?.numChildren > 0
    const groupDataHasChildren =
      item.groupData &&
      item.groupData.some((data) => !!(data.nodeData && data.nodeData?.numChildren > 0))
    const endIcon = nodeDataHasChildren || groupDataHasChildren ? undefined : false
    return { title, description, startIcon, endIcon }
  }

  const groupSearchResultsBy = (item: NodeAutocompleteResult) =>
    !!item.practitionerData
      ? t('component.serviceSearch.specialists')
      : t('component.serviceSearch.specialitiesAndServices')

  return (
    <ResponsiveModal
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      disableMobileClose
      omitDefaultPadding
      sx={{
        '& .MuiDialog-paper': {
          overflowY: 'visible',
        },
      }}
    >
      <SearchContainer isMobile={isMobile}>
        <Search
          searchFieldLabel={t('component.serviceSearch.searchFieldLabel')}
          popularData={popularData}
          allData={allData}
          breadcrumb={breadcrumb}
          onBreadcrumbChange={(crumb) => {
            if (typeof crumb === 'function') {
              onSelect(crumb([]))
            } else {
              onSelect(crumb)
            }
          }}
          onSelectAndClose={(item) => {
            if ('id' in item && item.id === callbackItemId) {
              openCallbackModal()
              return
            }
            onSelect(item)
          }}
          onClose={onClose}
          noteProvider={noteProvider}
          searchPending={false /* FIXME */}
          searchIndex={nodeSearch}
          getOptionLabel={(option) => option.keyword}
          optionListModifier={(options) => [
            ...options.filter((option) => !option.practitionerData),
            ...options.filter((option) => option.practitionerData),
          ]}
          backLabel={t('component.serviceSearch.backLabel')}
          useTracking
          open={true}
          breadcrumbHighlightProvider={breadcrumbHighlightProvider}
          mapDataToListProps={mapDataToListProps}
          mapSearchResultsToListProps={mapSearchResultsToListProps}
          groupSearchResultsBy={groupSearchResultsBy}
        />
      </SearchContainer>
    </ResponsiveModal>
  )
}

export default SearchSelect
